/**
 * Reusable Textarea Component
 * Supports multiline text input with error states
 */

import type { TextareaHTMLAttributes } from "react";
import { forwardRef } from "react";
import clsx from "clsx";

export interface TextareaProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helperText?: string;
  fullWidth?: boolean;
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    { label, error, helperText, fullWidth = false, className, id, ...props },
    ref,
  ) => {
    const textareaId =
      id || `textarea-${Math.random().toString(36).substr(2, 9)}`;
    const hasError = !!error;

    const containerStyles = fullWidth ? "w-full" : "";

    const textareaStyles = clsx(
      "block w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-0 transition-colors",
      {
        "border-gray-300 focus:border-blue-500 focus:ring-blue-500": !hasError,
        "border-red-500 focus:border-red-500 focus:ring-red-500": hasError,
        "bg-gray-100 cursor-not-allowed": props.disabled,
      },
      className,
    );

    return (
      <div className={containerStyles}>
        {label && (
          <label
            htmlFor={textareaId}
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            {label}
            {props.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}

        <textarea
          ref={ref}
          id={textareaId}
          className={textareaStyles}
          {...props}
        />

        {error && <p className="mt-1 text-sm text-red-600">{error}</p>}

        {helperText && !error && (
          <p className="mt-1 text-sm text-gray-500">{helperText}</p>
        )}
      </div>
    );
  },
);

Textarea.displayName = "Textarea";

export default Textarea;
